import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import axios from 'axios';

import { IInspectionDelayed } from 'app/shared/model/inspection-delayed.model';
import { IInspectionHistory, defaultValue } from 'app/shared/model/inspection-history.model';
import { IInspectionItemPerformed } from 'app/shared/model/inspection-item-performed.model';
import { IInspection } from 'app/shared/model/inspection.model';
import { EntityState, IQueryParams, createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from 'app/shared/util/entity-utils';

const initialState: EntityState<IInspectionHistory> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entitiesAlt: [] as ReadonlyArray<IInspectionDelayed>,
  countAlt: null,
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/inspection-histories';

// Actions

interface IInspectionHistoryPartialState extends IQueryParams {
  nameSearch?: string;
  inspectionHistoryId?: number;
}

export const getEntities = createAsyncThunk(
  'inspectionHistory/fetch_entity_list',
  async ({ page, size, sort, nameSearch, inspectionHistoryId }: IInspectionHistoryPartialState) => {
    const inspectionHistoryIdRequest = inspectionHistoryId ? `inspectionHistoryId=${inspectionHistoryId}&` : '';

    const requestUrl = `${apiUrl}${
      sort ? `?page=${page}&size=${size}&sort=${sort}&nameSearch=${nameSearch}&${inspectionHistoryIdRequest}` : '?'
    }cacheBuster=${new Date().getTime()}`;
    return axios.get<IInspectionHistory[]>(requestUrl);
  }
);

export const getEntity = createAsyncThunk(
  'inspectionHistory/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IInspectionHistory>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'inspectionHistory/create_entity',
  async (entity: IInspectionHistory, thunkAPI) => {
    const result = await axios.post<IInspectionHistory>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'inspectionHistory/update_entity',
  async (entity: IInspectionHistory, thunkAPI) => {
    const result = await axios.put<IInspectionHistory>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'inspectionHistory/partial_update_entity',
  async (entity: IInspectionHistory, thunkAPI) => {
    const result = await axios.patch<IInspectionHistory>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'inspectionHistory/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IInspectionHistory>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const getDelayedInspections = createAsyncThunk(
  'inspectionHistory/fetch_delayed_inspections',
  async ({ page, size, sort }: IQueryParams) => {
    const requestUrl = `${apiUrl}/delayed${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
    return axios.get<IInspectionDelayed[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getDelayedInspectionsCount = createAsyncThunk(
  'inspectionHistory/fetch_delayed_inspections_count',
  async () => {
    const requestUrl = `${apiUrl}/delayed/count`;
    return axios.get<number>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

interface IHistoryInspection {
  inspectionItemPerformed: IInspectionItemPerformed[];
  inspection: IInspection;
  assetId: string | number;
  observations: string;
}

export const createEntityByInspection = createAsyncThunk(
  'history/create_entity_by_inspection',
  async (entity: IHistoryInspection) => {
    const result = await axios.post<IHistoryInspection>(`${apiUrl}/by-inspection`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const InspectionHistorySlice = createEntitySlice({
  name: 'inspectionHistory',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getDelayedInspections), (state, action) => {
        return {
          ...state,
          loading: false,
          entitiesAlt: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getDelayedInspectionsCount), (state, action) => {
        return {
          ...state,
          countAlt: action.payload.data,
        };
      })
      .addMatcher(isFulfilled(createEntity, createEntityByInspection, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getDelayedInspections), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, createEntityByInspection, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = InspectionHistorySlice.actions;

// Reducer
export default InspectionHistorySlice.reducer;
